import { runtimeConfigSchema, type RuntimeConfig } from './schema'

export interface ConfigError {
  kind: 'fetch' | 'parse' | 'validation'
  message: string
  /** Human-readable `field: problem` entries for validation errors. */
  issues: string[]
}

export type ConfigLoadResult =
  { ok: true; config: RuntimeConfig } | { ok: false; error: ConfigError }

export const RUNTIME_CONFIG_URL = '/config.json'

/**
 * Loads and validates the runtime configuration. Never throws: failures are returned so the
 * bootstrap can render a configuration error instead of any feature route.
 */
export async function loadRuntimeConfig(
  url: string = RUNTIME_CONFIG_URL,
  fetchImpl: typeof fetch = fetch,
): Promise<ConfigLoadResult> {
  let response: Response
  try {
    response = await fetchImpl(url, { cache: 'no-store', headers: { Accept: 'application/json' } })
  } catch (cause) {
    return failure('fetch', `Could not fetch ${url}: ${describe(cause)}`)
  }
  if (!response.ok) {
    return failure('fetch', `Could not fetch ${url}: HTTP ${response.status}`)
  }

  let raw: unknown
  try {
    raw = await response.json()
  } catch (cause) {
    return failure('parse', `${url} is not valid JSON: ${describe(cause)}`)
  }

  return parseRuntimeConfig(raw)
}

export function parseRuntimeConfig(raw: unknown): ConfigLoadResult {
  const result = runtimeConfigSchema.safeParse(raw)
  if (result.success) return { ok: true, config: result.data }
  const issues = result.error.issues.map(
    (issue) => `${issue.path.length > 0 ? issue.path.join('.') : '(root)'}: ${issue.message}`,
  )
  return failure('validation', 'Runtime configuration is invalid', issues)
}

function failure(
  kind: ConfigError['kind'],
  message: string,
  issues: string[] = [],
): ConfigLoadResult {
  return { ok: false, error: { kind, message, issues } }
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause)
}
